import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../store/store';
import { logout } from '../auth/authSlice';
import type { FlashcardRunScope } from '../flashcardRun/flashcardRunSlice';
import type { ChatSurface } from '../../types/chatEnvelope';
import type { ChatStudySetKind } from '../../types/chatBlocks';

export type StudyMode = 'session' | 'standalone';
export type StudyReturnTo =
  | 'qbank-start'
  | 'topics'
  | 'home'
  | 'documents'
  | 'video'
  | 'flashcard-decks'
  | 'self-assessments'
  | 'faculty-student'
  // A study set launched from the companion (ORA-681). The way back is the
  // conversation the set was served in, not a list screen, so the return needs
  // more than a path: see `StudySetChatReturn`.
  | 'chat'
  | null;

/**
 * Where a study set launched from chat came from, and what to do on the way
 * back: the conversation to reopen, the block to scroll to, the surface that
 * was showing it, and the set to report the student's progress against.
 *
 * Persisted with the rest of the study context, so a reload in the middle of a
 * set-driven session still knows its way home.
 */
export interface StudySetChatReturn {
  conversationId: string;
  blockId: string;
  surface: ChatSurface;
  setId: string;
  /**
   * The runner the set belongs to. Carried so a runner of another kind — a
   * QBank block opened while a flashcard set is still the way back — does not
   * mistake the open set for its own (ORA-681 review).
   */
  kind: ChatStudySetKind;
}

// When a faculty admin reviews a student's self-assessment block, the block is
// owned by the student, so the standalone review loader must fetch it through
// the faculty-scoped endpoint instead of the current user's. This target
// carries the roster context needed for that fetch.
type FacultyReviewTarget = {
  cohortId: string;
  rosterEntryId: string;
};

interface StudyContextState {
  mode: StudyMode;
  activeBlockId: string | null;
  returnTo: StudyReturnTo;
  returnPath: string | null;
  returnSearch: string | null;
  flashcardScope: FlashcardRunScope | null;
  facultyReviewTarget: FacultyReviewTarget | null;
  chatReturn: StudySetChatReturn | null;
}

const initialState: StudyContextState = {
  mode: 'session',
  activeBlockId: null,
  returnTo: null,
  returnPath: null,
  returnSearch: null,
  flashcardScope: null,
  facultyReviewTarget: null,
  chatReturn: null,
};

const studyContextSlice = createSlice({
  name: 'studyContext',
  initialState,
  reducers: {
    enterStandalone: (
      state,
      action: PayloadAction<{
        /**
         * The QBank block this run opens, or null when the run has none — a
         * set-driven flashcard or video session (ORA-681). Never a placeholder
         * id: `getBlockId` falls back to the active block id and would try to
         * load it.
         */
        blockId: string | null;
        returnTo?: StudyReturnTo;
        returnPath?: string | null;
        returnSearch?: string | null;
        flashcardScope?: FlashcardRunScope | null;
        facultyReviewTarget?: FacultyReviewTarget | null;
        chatReturn?: StudySetChatReturn | null;
      }>
    ) => {
      state.mode = 'standalone';
      state.activeBlockId = action.payload.blockId ?? null;
      state.returnTo = action.payload.returnTo ?? 'qbank-start';
      state.returnPath = action.payload.returnPath ?? null;
      state.returnSearch = action.payload.returnSearch ?? null;
      state.flashcardScope = action.payload.flashcardScope ?? null;
      state.facultyReviewTarget = action.payload.facultyReviewTarget ?? null;
      state.chatReturn = action.payload.chatReturn ?? null;
    },
    exitStandalone: (state) => {
      state.mode = 'session';
      state.activeBlockId = null;
      state.returnTo = null;
      state.returnPath = null;
      state.returnSearch = null;
      state.flashcardScope = null;
      state.facultyReviewTarget = null;
      state.chatReturn = null;
    },
    setStandaloneBlockId: (state, action: PayloadAction<string | null>) => {
      state.activeBlockId = action.payload;
    },
    // Set only the return destination without entering standalone mode. Used by
    // session-mode flows (e.g. lecture videos) that still need a custom return
    // target after the user exits.
    setReturnTo: (state, action: PayloadAction<StudyReturnTo>) => {
      state.returnTo = action.payload;
      state.returnPath = null;
      state.returnSearch = null;
    },
    // Like setReturnTo but keeps an explicit return PATH — for session-mode flows
    // that must return to a specific place (e.g. lecture videos started from a
    // document's Study menu return to its /document/:id?page=N deep link, which
    // reopens the document on the companion).
    setReturnTarget: (
      state,
      action: PayloadAction<{
        returnTo: StudyReturnTo;
        returnPath?: string | null;
      }>
    ) => {
      state.returnTo = action.payload.returnTo;
      state.returnPath = action.payload.returnPath ?? null;
      state.returnSearch = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logout, () => initialState);
  },
});

export const selectStudyContext = (state: RootState) => state.studyContext;
export const selectStudyMode = (state: RootState) => state.studyContext.mode;
export const selectStudyReturnTo = (state: RootState) =>
  state.studyContext.returnTo;
export const selectStudyFlashcardScope = (state: RootState) =>
  state.studyContext.flashcardScope;
export const selectStudyFacultyReviewTarget = (state: RootState) =>
  state.studyContext.facultyReviewTarget;
export const selectStudySetChatReturn = (state: RootState) =>
  state.studyContext.chatReturn;
export const selectIsStandaloneStudy = (state: RootState) =>
  state.studyContext.mode === 'standalone';

export const {
  enterStandalone,
  exitStandalone,
  setStandaloneBlockId,
  setReturnTo,
  setReturnTarget,
} = studyContextSlice.actions;

export default studyContextSlice.reducer;
